import { AlertTriangle, Copy, Moon, Palette, RotateCcw, Sun, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  autoFixContrast,
  defaultColorsFor,
  findContrastIssues,
  type SavedTheme,
  type ThemeColorKey,
} from "@/lib/theme";
import { ColorField } from "./ColorField";
import { ThemePreview } from "./ThemePreview";

interface ThemeEditorProps {
  theme: SavedTheme;
  builtIn: boolean;
  onChange: (theme: SavedTheme) => void;
  onSave: () => void;
  onDiscard: () => void;
  onCreateCopy: () => void;
  onDelete: () => void;
  dirty: boolean;
  saving: boolean;
  disabled?: boolean;
}

const BASIC_FIELDS: Array<[ThemeColorKey, string, string]> = [
  ["background", "Background", "The main canvas behind every workspace."],
  ["surface", "Surface", "Navigation, panels and primary containers."],
  ["text", "Primary text", "Headings, labels and essential information."],
  ["accent", "Accent", "Primary actions, selection and focus."],
];

const ADVANCED_FIELDS: Array<[ThemeColorKey, string, string]> = [
  ["elevated", "Elevated surface", "Inputs, media wells and secondary layers."],
  ["muted", "Secondary text", "Descriptions, metadata and placeholder copy."],
  ["border", "Border", "Dividers and control boundaries."],
  ["success", "Success", "Completed and healthy states."],
  ["warning", "Warning", "Attention and cost-sensitive states."],
  ["danger", "Danger", "Errors and destructive actions."],
];

const ACCENT_SUGGESTIONS = ["#2563EB", "#4F7CFF", "#7C5CE7", "#087F8C", "#B45309"];

export function ThemeEditor({
  theme,
  builtIn,
  onChange,
  onSave,
  onDiscard,
  onCreateCopy,
  onDelete,
  dirty,
  saving,
  disabled,
}: ThemeEditorProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const contrastIssues = findContrastIssues(theme.colors);
  const ModeIcon = theme.mode === "light" ? Sun : Moon;

  function updateColor(key: ThemeColorKey, color: string) {
    onChange({ ...theme, colors: { ...theme.colors, [key]: color.toUpperCase() } });
  }

  return (
    <section className="overflow-hidden rounded-xl border border-studio-border bg-studio-panel shadow-panel">
      <div className="border-b border-studio-border px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-studio-accent-soft text-studio-accent">
              <Palette className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-sm font-semibold text-studio-ink">{theme.name}</h2>
                <span className="inline-flex items-center gap-1 rounded-full border border-studio-border bg-studio-card px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-studio-muted">
                  <ModeIcon className="h-3 w-3" /> {theme.mode}
                </span>
                {builtIn && <span className="font-mono text-[9px] uppercase tracking-wide text-studio-accent">Built in</span>}
              </div>
              <p className="mt-1 max-w-xl text-xs leading-5 text-studio-muted">
                {builtIn
                  ? "Built-in themes stay untouched. Create a copy to make it yours."
                  : "Edit this personal palette. Supporting hover and soft colors are generated automatically."}
              </p>
            </div>
          </div>
          {builtIn && (
            <Button type="button" size="sm" disabled={disabled} onClick={onCreateCopy}>
              <Copy className="h-3.5 w-3.5" /> Create from this theme
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[minmax(0,1.08fr)_minmax(340px,0.92fr)]">
        <div className="border-b border-studio-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
          {!builtIn && (
            <label className="mb-4 block border-b border-studio-border pb-4 text-xs font-semibold text-studio-muted">
              Theme name
              <Input
                className="mt-2"
                value={theme.name}
                maxLength={40}
                disabled={disabled}
                onChange={(event) => onChange({ ...theme, name: event.target.value })}
              />
            </label>
          )}

          <div className="pt-1">
            {BASIC_FIELDS.map(([key, label, description]) => (
              <ColorField
                key={key}
                id={`${theme.id}-${key}`}
                label={label}
                description={description}
                value={theme.colors[key]}
                disabled={disabled || builtIn}
                suggestions={key === "accent" ? ACCENT_SUGGESTIONS : undefined}
                onChange={(color) => updateColor(key, color)}
              />
            ))}
          </div>

          <details className="group border-t border-studio-border">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between py-3 text-xs font-semibold text-studio-ink focus-visible:outline-none focus-visible:text-studio-accent">
              Advanced colors
              <span className="font-mono text-[10px] font-medium text-studio-muted group-open:hidden">SHOW</span>
              <span className="hidden font-mono text-[10px] font-medium text-studio-muted group-open:inline">HIDE</span>
            </summary>
            <div>
              {ADVANCED_FIELDS.map(([key, label, description]) => (
                <ColorField
                  key={key}
                  id={`${theme.id}-${key}`}
                  label={label}
                  description={description}
                  value={theme.colors[key]}
                  disabled={disabled || builtIn}
                  onChange={(color) => updateColor(key, color)}
                />
              ))}
            </div>
          </details>

          <div className="mt-4 border-t border-studio-border pt-4">
            {contrastIssues.length > 0 ? (
              <div className="mb-4 flex items-start gap-3 rounded-lg bg-studio-warning-soft px-3 py-3 text-studio-warning-ink">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold">Contrast needs attention</p>
                  <p className="mt-1 text-xs leading-5">
                    {contrastIssues.length} color {contrastIssues.length === 1 ? "pair is" : "pairs are"} below WCAG AA.
                  </p>
                  {!builtIn && (
                    <Button className="mt-2" type="button" variant="outline" size="sm" onClick={() => onChange({ ...theme, colors: autoFixContrast(theme.colors) })}>
                      Auto-fix contrast
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-4 flex items-center gap-2 text-xs font-medium text-studio-success">
                <span className="h-2 w-2 rounded-full bg-studio-success" />
                Core text and status colors meet WCAG AA.
              </div>
            )}

            {!builtIn && (
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-1">
                  <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange({ ...theme, colors: defaultColorsFor(theme.mode) })}>
                    <RotateCcw className="h-3.5 w-3.5" /> Restore {theme.mode} base
                  </Button>
                  {confirmDelete ? (
                    <>
                      <Button type="button" variant="danger" size="sm" disabled={disabled} onClick={onDelete}>Confirm delete</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>Cancel</Button>
                    </>
                  ) : (
                    <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => setConfirmDelete(true)}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={!dirty || disabled} onClick={onDiscard}>Discard changes</Button>
                  <Button type="button" size="sm" loading={saving} disabled={!dirty || disabled || !theme.name.trim() || contrastIssues.length > 0} onClick={onSave}>Save theme</Button>
                </div>
              </div>
            )}
            {builtIn && dirty && (
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onDiscard}>Discard selection</Button>
                <Button type="button" size="sm" loading={saving} disabled={disabled} onClick={onSave}>Apply theme</Button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-studio-card/40 p-4 sm:p-5">
          <ThemePreview mode={theme.mode} colors={theme.colors} />
        </div>
      </div>
    </section>
  );
}
